// Core parameter types and interfaces for the dynamic parameter system

// NOTE: ConvertType from useFormatNumber.tsx is NOT imported here to avoid
// pulling React hook dependencies into the Potree Rollup build.
// (rollup-plugin-typescript2 resolves even `import type` for type-checking)
// Instead, we use `number` which is equivalent since ConvertType is a numeric enum.

// Import and re-export gradient types from shared-types (avoids build-time circular dependency with potree_fork)
import type { GradientType, GradientsType, GradientDefinition } from 'shared-types';
export type { GradientType, GradientsType, GradientDefinition };

export enum PropertyValueType {
  FLOAT = 'FLOAT',
  INT = 'INT',
  STRING = 'STRING',
  BOOLEAN = 'BOOLEAN',
  SELECT = 'SELECT',
  COLOR = 'COLOR',
  GRADIENT = 'GRADIENT',
  RANGE = 'RANGE',
  MIN_MAX = 'MIN_MAX',
  COORDINATE = 'COORDINATE',
  JSON = 'JSON',
  FLOAT_ARRAY = 'FLOAT_ARRAY',
}

export type PropertyValueMap = {
  [PropertyValueType.FLOAT]: number;
  [PropertyValueType.INT]: number;
  [PropertyValueType.STRING]: string;
  [PropertyValueType.BOOLEAN]: boolean;
  [PropertyValueType.SELECT]: string;
  [PropertyValueType.COLOR]: string;
  [PropertyValueType.GRADIENT]: GradientType;
  [PropertyValueType.RANGE]: number;
  [PropertyValueType.MIN_MAX]: [number, number];
  [PropertyValueType.COORDINATE]: { x: number; y: number; z: number };
  [PropertyValueType.JSON]: string;
  [PropertyValueType.FLOAT_ARRAY]: number[];
};

// Union type for all possible property values
export type PropertyValueData = PropertyValueMap[PropertyValueType];

/**
 * Declarative condition for controlling property visibility.
 * A property with visibility conditions is only shown when ALL conditions are satisfied.
 */
export interface VisibilityCondition {
  /** Identifier of the property whose value determines visibility.
   *  Supports `{objectIdentifier}` template which is replaced with the current object's identifier at runtime.
   *  Example: `clip_method.{objectIdentifier}` resolves to `clip_method.plane_abc` */
  propertyIdentifier: string;
  /** The value(s) the controlling property must have for this property to be visible.
   *  If an array, the property is visible when the controlling value matches ANY element (OR). */
  value: PropertyValueData | PropertyValueData[];
  /** When true, inverts the condition (visible when value does NOT match) */
  negate?: boolean;
}

/**
 * Props passed to custom property input components registered via PropertyUIConfig.customComponent.
 * Generic over the property's `valueType` so `value` and `onChange` are typed accordingly.
 */
export interface CustomPropertyInputProps<T extends PropertyValueType = PropertyValueType> {
  instance: PropertyInstance & { value: PropertyValueMap[T] };
  onChange: (value: PropertyValueMap[T]) => void;
  disabled?: boolean;
  /** All sibling property instances in the current panel, for cross-property reads */
  allInstances?: PropertyInstance[];
  /** The project ID the panel is scoped to — needed by custom components that issue project-level mutations */
  projectId?: string;
}

/** Map of property identifiers to their values */
export type PropertyValueRecord = Record<string, PropertyValueData>;

export enum AnnotationType {
  POINT = 'POINT',
  DISTANCE = 'DISTANCE',
  AREA = 'AREA',
  BOX = 'BOX',
  POINTCLUSTER = 'POINTCLUSTER',
  PLANE = 'PLANE',
  ROOM = 'ROOM',
  FLOOR = 'FLOOR',
  WALL_SKETCH = 'WALL_SKETCH',
  OPENING = 'OPENING',
  /** OPENING_SKETCH is a door/window rendered as a line on a wall-sketch edge
   *  (layout persona), distinct from the floorplan OPENING polygon. */
  OPENING_SKETCH = 'OPENING_SKETCH',
  OUTLINE = 'OUTLINE',
  STRUCTURE = 'STRUCTURE',
  /** SKETCH_OBJECT is a furniture/fixture symbol (bath, chair, cabinet, …) placed
   *  in the 2D floor layout. The object subtype comes from a client-side code
   *  registry (`sketchobject_type_id`); all geometry + params live in reactive
   *  properties. */
  SKETCH_OBJECT = 'SKETCH_OBJECT',
  /** SURFACE is persisted as a group (GroupType.Surface), not a standalone annotation,
   *  but it participates in the annotationTypes-based property registry (filter/clip/heatmap)
   *  exactly like the former PLANE annotation. */
  SURFACE = 'SURFACE',
}

/**
 * Defines where property values are stored in the database hierarchy
 * Each level determines the scope and persistence of the property value
 */
export enum PropertyType {
  /** Stored per user within a specific project - each user has their own values for the same project */
  PROJECT_USER = 'STATE_BY_USER',
  /** Stored globally for the entire project - all users see the same values */
  PROJECT_GLOBAL = 'GLOBAL_STATE',
  /** Stored at the organization level - shared across all projects in the organization */
  ORGANIZATION = 'ORGANIZATION',
  /** Stored in user's personal settings - applies to the user across all projects and organizations */
  USER_SETTINGS = 'USER',
}

export interface PropertyConfig_<T extends PropertyValueType = PropertyValueType> {
  /** Unique identifier used to reference this property throughout the application */
  readonly identifier: string;
  /** Human-readable display name shown in the UI */
  name: string;
  /** Optional description text displayed as a tooltip to help users understand the property's purpose */
  description?: string;
  /** The data type of this property (e.g., FLOAT, STRING, BOOLEAN) which determines the input control */
  valueType: T;
  /** The initial value assigned to this property when it's first created */
  defaultValue: PropertyValueMap[T];
  /** Optional function that returns a dynamic default value (e.g. user preference).
   *  When provided, called instead of using defaultValue for new property instances. */
  getDefaultValue?: () => PropertyValueMap[T];
  /** Optional validation rules to ensure property values meet specific criteria before being saved */
  validation?: PropertyValidation;
  /** Optional UI configuration to customize how this property appears and behaves in the interface */
  ui?: PropertyUIConfig;
  /** Optional CSS class names to apply custom styling to this property's UI component */
  className?: string;
  /** When true, the property input is disabled and users cannot modify its value */
  disabled?: boolean;
  /** When true, the property is displayed as read-only text (no input control) */
  readOnly?: boolean;
  /** Format function for read-only display values (e.g. formatting distance with units).
   *  Only used when readOnly is true. Falls back to String(value) if not provided. */
  formatValue?: (value: PropertyValueData) => string;
  /** When true, the property identifier is a template that contains `{objectIdentifier}`.
   *  PropertyPanel will resolve it per-annotation, e.g. `filter.{objectIdentifier}` → `filter.plane_abc`.
   *  The resolved identifier is used for the PropertyService key. */
  dynamicIdentifier?: boolean;
  /** Override the objectIdentifier used when reading/writing this property in the PropertyService.
   *  By default PropertyPanel uses the annotation identifier. Set this when the property
   *  is stored under a fixed objectIdentifier (e.g. `'toggles'` or `'filters'`). */
  objectIdentifierOverride?: string;
}

export interface ProjectUserPropertyConfig<T extends PropertyValueType = PropertyValueType> extends PropertyConfig_<T> {
  type: PropertyType.PROJECT_USER;
  /** Specifies which annotation types this property applies to (empty array means it applies to all annotation types) */
  annotationTypes: AnnotationType[];
}

export interface ProjectGlobalPropertyConfig<T extends PropertyValueType = PropertyValueType> extends PropertyConfig_<T> {
  type: PropertyType.PROJECT_GLOBAL;
  /** Specifies which annotation types this property applies to (empty array means it applies to all annotation types) */
  annotationTypes: AnnotationType[];
}

export interface UserPropertyConfig<T extends PropertyValueType = PropertyValueType> extends PropertyConfig_<T> {
  type: PropertyType.USER_SETTINGS;
}

export interface OrganisationPropertyConfig<T extends PropertyValueType = PropertyValueType> extends PropertyConfig_<T> {
  type: PropertyType.ORGANIZATION;
}

export type PropertyConfig<T extends PropertyValueType = PropertyValueType> = ProjectUserPropertyConfig<T> | ProjectGlobalPropertyConfig<T> | UserPropertyConfig<T> | OrganisationPropertyConfig<T>;

export interface PropertyValidation {
  /** When true, the property must have a value and cannot be empty or undefined */
  required?: boolean;
  /** Minimum allowed value for numeric properties */
  min?: number;
  /** Maximum allowed value for numeric properties */
  max?: number;
  /** Increment/decrement step size for numeric input controls */
  step?: number;
  /** Regular expression pattern that string values must match to be valid */
  pattern?: RegExp;
  /** Available choices for SELECT type properties, including their display labels and enabled state */
  options?: Array<{
    value: PropertyValueData;
    label: string;
    disabled?: boolean;
    /** When set, this option is only shown for the specified annotation types */
    annotationTypes?: AnnotationType[];
  }>;
  /** Custom validation function that returns an error message if validation fails, or undefined if valid */
  custom?: (value: PropertyValueData) => string | undefined;
}

export interface PropertyUIConfig {
  /** Override the default display label with a custom one for this property */
  label?: string;
  /** When true, the label is hidden in the UI (useful for properties with self-explanatory controls) */
  hideLabel?: boolean;
  /** Placeholder text shown in empty input fields to guide user input */
  placeholder?: string;
  /** Custom unit suffix (e.g., '%', 'items'). If set, takes precedence over user preference unit. */
  customUnit?: string;
  /** When true, hides the unit display. By default, units are shown.
      Only implemented for MinMax right now, expand when required */
  hideUnit?: boolean;
  /** When true, formats numeric values according to user measurement preferences (meter/feet). Defaults to false.
      Only implemented for MinMax right now, expand when required  */
  useUserPreferenceConvertType?: number;
  /** When true, applies gradient background styling to the property control */
  useGradientStyling?: boolean;
  /** React component to display as an icon next to the property label (only shown when tooltip is present) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: (props: any) => any;
  /** Additional help text displayed as a tooltip on hover */
  tooltip?: string;
  /** Group name to organize related properties together in the UI */
  group?: string;
  /** Numeric order to control the display sequence of the whole group relative to other groups
      (lower numbers appear first). A group takes the smallest groupOrder among its properties. */
  groupOrder?: number;
  /** Numeric order to control the display sequence within a group (lower numbers appear first) */
  order?: number;
  /** When true, displays a reset button that restores the default value */
  showResetButton?: boolean;
  /** Custom handler function called when the reset button is clicked */
  onReset?: (propertyInstance: PropertyInstance) => void;
  /** When true, displays minimum and maximum value labels below range sliders */
  showRangeLabels?: boolean;
  /** Number of decimal places to show for float values */
  decimals?: number;
  /** When true, this boolean property acts as a toggle for its entire group.
      The property will be rendered as a checkbox in the group header, and when false,
      all other properties in the group will be disabled. Only valid for BOOLEAN properties. */
  isGroupToggle?: boolean;
  /** When true, this property is not rendered in the panel even though it exists on the annotation */
  hidden?: boolean;
  /** When true, this property is hidden in the combined group/multi-select panel (shown only for a single object). */
  hiddenInGroup?: boolean;
  /** Declarative visibility conditions. Property is only shown when ALL conditions are satisfied.
   *  Use this to show/hide properties based on the value of other properties. */
  visibilityConditions?: VisibilityCondition[];
  /** Custom React component to render instead of the default PropertyInput.
   *  The component receives CustomPropertyInputProps.
   *  Typed as generic function to avoid pulling React into the Potree Rollup build. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customComponent?: (props: CustomPropertyInputProps) => any;
}

export interface PropertyInstance_ {
  /** The current value of this property instance */
  value: PropertyValueData;
  /** Unique identifier of the object (annotation, project, etc.) this property value belongs to */
  objectIdentifier?: string;
  /** Validation error message to display when the current value doesn't meet validation criteria */
  error?: string;
  /** When true, indicates the property value is being loaded from the database */
  loading?: boolean;
  /** When true, indicates multiple selected objects have different values for this property */
  isMixed?: boolean;
}

export interface ProjectUserPropertyInstance extends PropertyInstance_ {
  /** The complete configuration object that defines this property's behavior and appearance */
  config: ProjectUserPropertyConfig;
  /** User ID associated with this property value */
  userId: string;
}

export interface ProjectGlobalPropertyInstance extends PropertyInstance_ {
  /** The complete configuration object that defines this property's behavior and appearance */
  config: ProjectGlobalPropertyConfig;
}

export interface UserPropertyInstance extends PropertyInstance_ {
  /** The complete configuration object that defines this property's behavior and appearance */
  config: UserPropertyConfig;
  /** User ID this property belongs to */
  userId: string;
}

export interface OrganisationPropertyInstance extends PropertyInstance_ {
  /** The complete configuration object that defines this property's behavior and appearance */
  config: OrganisationPropertyConfig;
}

export type PropertyInstance = ProjectUserPropertyInstance | ProjectGlobalPropertyInstance | UserPropertyInstance | OrganisationPropertyInstance;

export interface PropertyUpdateEvent {
  /** The unique identifier of the property being updated */
  identifier: string;
  /** The new value being set for the property */
  value: PropertyValueData;
  /** Identifier of the object this update applies to (if applicable) */
  objectIdentifier?: string;
  /** ID of the user making this update (for user-specific properties) */
  userId?: string;
  /** The state binding level that determines where this update will be stored */
  propertyType: PropertyType;
}

export interface PropertyHookConfig {
  /** The project ID to scope property operations to */
  projectId: string;
  /** Optional filter to only include properties for specific annotation types */
  annotationTypes?: AnnotationType[];
  /** Optional filter to only include properties for specific object identifiers */
  objectIdentifiers?: string[];
}

export interface PropertyHookConfigWithCallbacks extends PropertyHookConfig {
  /** Optional callback functions to handle property lifecycle events and validation */
  callbacks?: PropertyCallbacks;
}

export interface PropertyCallbacks {
  /** Triggered when a property value is changed by the user or system */
  onUpdate?: (event: PropertyUpdateEvent) => void;
  /** Triggered when a property value fails validation, providing the error details */
  onValidationError?: (identifier: string, error: string) => void;
  /** Triggered before a property update is applied - return false to cancel the update */
  beforeUpdate?: (event: PropertyUpdateEvent) => boolean;
  /** Triggered after a property update has been successfully saved to the database */
  afterUpdate?: (event: PropertyUpdateEvent) => void;
}

/** Represents a property value as stored in the database via GraphQL */
export interface PropertyValue {
  /** The unique identifier of the property this value belongs to */
  identifier: string;
  /** The actual stored value data */
  value: PropertyValueData;
  /** The data type of the stored value */
  valueType: PropertyValueType;
  /** Optional identifier linking this value to a specific object */
  objectIdentifier?: string;
  /** ISO timestamp of when this value was first created */
  createdAt: string;
  /** ISO timestamp of when this value was last modified */
  updatedAt: string;
}

/** Central registry for managing property definitions across the application */
export interface PropertyRegistry {
  /** Registers a new property configuration in the system */
  register(config: PropertyConfig): void;
  /** Removes a property configuration from the registry */
  unregister(identifier: string): void;
  /** Checks if a property with the given identifier is currently registered */
  isRegistered(identifier: string): boolean;
  /** Retrieves the configuration for a specific property by its identifier */
  getConfig(identifier: string): PropertyConfig | undefined;
  /** Returns all registered property configurations */
  getAllConfigs(): PropertyConfig[];
  /** Returns property configurations that apply to any of the specified annotation types */
  getConfigsForAnnotationTypes(annotationTypes: AnnotationType[]): PropertyConfig[];
  /** Attaches (or replaces) a custom UI component on an already-registered property.
   *  Pass the matching `PropertyValueType` as `T` to type `instance.value`/`onChange`. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setCustomComponent<T extends PropertyValueType = PropertyValueType>(identifier: string, component: ((props: CustomPropertyInputProps<T>) => any) | undefined): void;
  /** Validates a property value against its registered configuration - returns error message if invalid, undefined if valid */
  validateValue(identifier: string, value: PropertyValueData): string | undefined;
}
